import { show } from "~/api/contractBalance.js";
import * as types from "../mutation-types";
import store from "../../store";

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function resolveContractBalancePayload(payload, contractId) {
  if (Array.isArray(payload)) {
    const matchedById = payload.find(
      (item) => Number(item?.id) === Number(contractId)
    );
    return matchedById || payload[0] || null;
  }

  if (payload && typeof payload === "object") {
    return payload;
  }

  const primitive = toNumber(payload);
  if (primitive !== null) {
    return {
      id: contractId,
      balance: primitive
    };
  }

  return null;
}

function extractBalanceValue(balanceData) {
  if (balanceData === null || balanceData === undefined) {
    return 0;
  }

  if (typeof balanceData === "number") {
    return Number.isFinite(balanceData) ? balanceData : 0;
  }

  if (typeof balanceData === "string") {
    const normalized = balanceData.replace(",", ".").trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (typeof balanceData === "object") {
    const parsed =
      toNumber(balanceData.balance) ??
      toNumber(balanceData.amount) ??
      toNumber(balanceData.total) ??
      toNumber(balanceData.value) ??
      toNumber(balanceData.currentBalance);

    return parsed === null ? 0 : parsed;
  }

  return 0;
}

export const state = {
  amount: null,
  hoursLeft: null,
  isLoading: false,
  updatedAt: null
};

export const mutations = {
  [types.SET_IS_LOADING_CONTRACT_BALANCE_AMOUNT](state, status) {
    state.isLoading = status;
  },
  [types.SET_CONTRACT_BALANCE_AMOUNT](state, data) {
    state.amount = data;
  },
  [types.SET_CONTRACT_BALANCE_UPDATED_AT](state, timestamp) {
    state.updatedAt = timestamp;
  },
  [types.SET_CONTRACT_BALANCE_HOURS_LEFT](state, hoursLeft) {
    state.hoursLeft = hoursLeft;
  }
};

export const actions = {
  async fetchContractBalanceAmount({ commit }, payloadOrContractId = null) {
    commit(types.SET_IS_LOADING_CONTRACT_BALANCE_AMOUNT, true);

    const isObject = payloadOrContractId !== null
      && typeof payloadOrContractId === "object";
    const contractId = isObject
      ? payloadOrContractId.contractId
      : payloadOrContractId;
    const estimate = isObject
      ? Boolean(payloadOrContractId.estimate)
      : false;

    const activeContractId =
      contractId || store.getters["contract/activeContract"]?.id;

    if (!activeContractId) {
      commit(types.SET_IS_LOADING_CONTRACT_BALANCE_AMOUNT, false);
      return null;
    }

    try {
      const response = await show(activeContractId, { estimate });
      const payload = resolveContractBalancePayload(
        response?.data?.data,
        activeContractId
      );

      commit(types.SET_CONTRACT_BALANCE_AMOUNT, payload);
      commit(types.SET_CONTRACT_BALANCE_UPDATED_AT, Date.now());

      if (estimate && payload) {
        const rawHoursLeft = toNumber(payload.hoursLeft)
          ?? toNumber(payload.hours_left);
        commit(types.SET_CONTRACT_BALANCE_HOURS_LEFT, rawHoursLeft);
      }

      return response;
    } finally {
      commit(types.SET_IS_LOADING_CONTRACT_BALANCE_AMOUNT, false);
    }
  }
};

export const getters = {
  amount: (state) => state.amount,
  value: (state) => extractBalanceValue(state.amount),
  hoursLeft: (state) => state.hoursLeft,
  updatedAt: (state) => state.updatedAt,
  isLoading: (state) => state.isLoading
};
